import React, { useRef } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle } from "lucide-react";

import Breadcrumb from "../../../../components/Breadcrum/Breadcrum";
import ImageGallery from "../../../../components/ImageGallery/ImageGallery";
import automobiles from "../../../../data/automobileData";

import "./CarDetails.css";

export default function CarDetails() {
    const { slug } = useParams();

    const car = automobiles.find((item) => item.slug === slug);

    const overviewRef = useRef(null);
    const exteriorRef = useRef(null);
    const interiorRef = useRef(null);
    const performanceRef = useRef(null);
    const technologyRef = useRef(null);
    const safetyRef = useRef(null);
    const specificationRef = useRef(null);
    const galleryRef = useRef(null);

    const scrollToSection = (ref) => {
        ref.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    if (!car) {
        return (
            <div className="container py-5">
                <h2>Vehicle Not Found</h2>
            </div>
        );
    }

    return (
        <>
            <Breadcrumb
                title={car.name}
                backgroundImage="/assets/img/contact-banner.png"
                height="45vh"
            />

            <section className="car-details">

                <div className="container">

                    {/* HERO */}

                    <div
                        className="car-hero"
                        ref={overviewRef}
                    >

                        <div className="car-hero-image">
                            <img src={car.image} alt={car.name} />
                        </div>

                        <div className="car-info">

                            {/* <span className="car-badge">
                                Premium Automobile
                            </span> */}

                            <h1>{car.name}</h1>

                            <p>
                                {car.description}
                            </p>

                        </div>

                    </div>

                    {/* STICKY MENU */}

                    <div className="sticky-nav">

                        <button onClick={() => scrollToSection(overviewRef)}>
                            Overview
                        </button>

                        <button onClick={() => scrollToSection(exteriorRef)}>
                            Exterior
                        </button>

                        <button onClick={() => scrollToSection(interiorRef)}>
                            Interior
                        </button>

                        <button onClick={() => scrollToSection(performanceRef)}>
                            Performance
                        </button>

                        <button onClick={() => scrollToSection(technologyRef)}>
                            Technology
                        </button>

                        <button onClick={() => scrollToSection(safetyRef)}>
                            Safety
                        </button>

                        <button onClick={() => scrollToSection(specificationRef)}>
                            Specs
                        </button>

                        <button onClick={() => scrollToSection(galleryRef)}>
                            Gallery
                        </button>

                    </div>

                    {/* OVERVIEW */}

                    <section
                        className="car-section"
                        ref={overviewRef}
                    >

                        <div className="section-title">

                            <h2>Overview</h2>

                        </div>

                        <p className="overview-text">
                            {car.description}
                        </p>

                    </section>

                    {/* EXTERIOR */}

                    <section
                        className="car-section"
                        ref={exteriorRef}
                    >

                        <div className="section-title">

                            <h2>Exterior</h2>

                        </div>

                        <div className="feature-grid">

                            {car.exterior.map((item, index) => (

                                <div
                                    className="feature-item"
                                    key={index}
                                >

                                    <CheckCircle size={20} />

                                    <span>{item}</span>

                                </div>

                            ))}

                        </div>

                    </section>

                    {/* INTERIOR */}

                    <section
                        className="car-section"
                        ref={interiorRef}
                    >

                        <div className="section-title">

                            <h2>Interior</h2>

                        </div>

                        <div className="feature-grid">

                            {car.interior.map((item, index) => (

                                <div
                                    className="feature-item"
                                    key={index}
                                >

                                    <CheckCircle size={20} />

                                    <span>{item}</span>

                                </div>

                            ))}

                        </div>

                    </section>

                    {/* PERFORMANCE */}

                    <section
                        className="car-section"
                        ref={performanceRef}
                    >

                        <div className="section-title">
                            <h2>Performance</h2>
                        </div>

                        <div className="feature-grid">

                            {car.performance.map((item, index) => (

                                <div
                                    className="feature-item"
                                    key={index}
                                >
                                    <CheckCircle size={20} />
                                    <span>{item}</span>
                                </div>

                            ))}

                        </div>

                    </section>

                    {/* TECHNOLOGY */}

                    <section
                        className="car-section"
                        ref={technologyRef}
                    >

                        <div className="section-title">
                            <h2>Technology</h2>
                        </div>

                        <div className="feature-grid">

                            {car.technology.map((item, index) => (

                                <div
                                    className="feature-item"
                                    key={index}
                                >
                                    <CheckCircle size={20} />
                                    <span>{item}</span>
                                </div>

                            ))}

                        </div>

                    </section>

                    {/* SAFETY */}

                    <section
                        className="car-section"
                        ref={safetyRef}
                    >

                        <div className="section-title">
                            <h2>Safety</h2>
                        </div>

                        <div className="feature-grid">

                            {car.safety.map((item, index) => (

                                <div
                                    className="feature-item"
                                    key={index}
                                >
                                    <CheckCircle size={20} />
                                    <span>{item}</span>
                                </div>

                            ))}

                        </div>

                    </section>

                    {/* SPECIFICATIONS */}

                    <section
                        className="car-section"
                        ref={specificationRef}
                    >

                        <div className="section-title">
                            <h2>Technical Specifications</h2>
                        </div>

                        <div className="spec-grid">

                            {Object.entries(car.specifications).map(([key, value]) => (

                                <div
                                    className="spec-card"
                                    key={key}
                                >

                                    <h4>{key}</h4>

                                    <p>{value}</p>

                                </div>

                            ))}

                        </div>

                    </section>

                    {/* IMAGE GALLERY */}

                    <section
                        className="car-section"
                        ref={galleryRef}
                    >

                        <div className="section-title">
                            <h2>Image Gallery</h2>
                        </div>

                        <ImageGallery
                            images={car.gallery}
                            carName={car.name}
                        />

                    </section>

                </div>

            </section>

        </>
    );

}